import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { Observable } from 'rxjs';
import { QAFlowAnalysisStatus, QAFlowApplyFix, QAFlowCaptureManifest, QAFlowCaptureStart, QAFlowCaptureStatus, QAFlowPathBundle, QAFlowRunConfig, QAFlowRunFinalize, QAFlowRunRecord, QAFlowRunStart, QAFlowRunStatus, QAFlowSummary } from '../models/qaflow';

@Injectable({
  providedIn: 'root'
})
export class QAFlowService {

  baseUrl = environment.apiEndpoint + '/qaflow/';

  constructor(private http: HttpClient) { }

  startRun(projectId: string, config: QAFlowRunConfig): Observable<QAFlowRunStart> {
    return this.http.post<QAFlowRunStart>(`${this.baseUrl}${projectId}/run`, config);
  }

  /**
   * Polled every 10s while sim data is generating. The adaptive sim loop's
   * round-stepping is entirely server-side and hidden behind this call — a
   * "running" response may mean a brand-new Decipher batch just started
   * underneath, not just the same one still in progress.
   */
  getSimRunStatus(projectId: string, runId: string): Observable<QAFlowRunStatus> {
    return this.http.get<QAFlowRunStatus>(`${this.baseUrl}${projectId}/run/${runId}/sim-status`);
  }

  getSummary(projectId: string): Observable<QAFlowSummary> {
    return this.http.get<QAFlowSummary>(`${this.baseUrl}${projectId}/summary`);
  }

  getRuns(projectId: string): Observable<QAFlowRunRecord[]> {
    return this.http.get<QAFlowRunRecord[]>(`${this.baseUrl}${projectId}/runs`);
  }

  finalizeRun(projectId: string, runId: string, body: QAFlowRunFinalize): Observable<QAFlowRunRecord> {
    return this.http.put<QAFlowRunRecord>(`${this.baseUrl}${projectId}/run/${runId}`, body);
  }

  clearRuns(projectId: string): Observable<{ cleared: number }> {
    return this.http.delete<{ cleared: number }>(`${this.baseUrl}${projectId}/runs`);
  }

  getPaths(projectId: string, runId: string): Observable<QAFlowPathBundle> {
    return this.http.get<QAFlowPathBundle>(`${this.baseUrl}${projectId}/run/${runId}/paths`);
  }

  /** The persisted capture manifest for a run; 404s until a capture case exists and finishes. */
  getCaptureManifest(projectId: string, runId: string): Observable<QAFlowCaptureManifest> {
    return this.http.get<QAFlowCaptureManifest>(`${this.baseUrl}${projectId}/run/${runId}/capture/manifest`);
  }

  /** Launches the capture pipeline for a run's required-runs bundle. Starts real ECS tasks. */
  startCapture(projectId: string, runId: string): Observable<QAFlowCaptureStart> {
    return this.http.post<QAFlowCaptureStart>(`${this.baseUrl}${projectId}/run/${runId}/capture`, {});
  }

  /** Polled while capture is in flight; "none" if no capture case exists yet for this run. */
  getCaptureStatus(projectId: string, runId: string): Observable<QAFlowCaptureStatus> {
    return this.http.get<QAFlowCaptureStatus>(`${this.baseUrl}${projectId}/run/${runId}/capture`);
  }

  /**
   * Zips the run's captured media — question screenshots ("Completes Media"),
   * plus every term point's media too when includeTerms ("Completes & Terms
   * Media") — straight from the persisted capture manifest and returns a
   * presigned download url, cached server-side after the first call per
   * (runId, includeTerms) pair.
   */
  downloadCapture(projectId: string, runId: string, includeTerms: boolean): Observable<string> {
    return this.http.get(`${this.baseUrl}${projectId}/run/${runId}/capture/download?includeTerms=${includeTerms}`, { responseType: 'text' });
  }

  /**
   * Just the run's AI analysis and its state — polled while the errors detail is
   * open, so it deliberately doesn't re-fetch the run list.
   */
  getAnalysis(projectId: string, runId: string): Observable<QAFlowAnalysisStatus> {
    return this.http.get<QAFlowAnalysisStatus>(`${this.baseUrl}${projectId}/run/${runId}/analysis`);
  }

  /** Writes one AI-proposed fix into the survey's XML in Decipher. Modifies the survey. */
  applyFix(projectId: string, runId: string, fixIndex: number): Observable<QAFlowApplyFix> {
    return this.http.post<QAFlowApplyFix>(`${this.baseUrl}${projectId}/run/${runId}/fix/${fixIndex}/apply`, {});
  }
}
